// src/components/managed-sources/DataArrivalSettings.tsx
//
// The two customer settings that describe HOW a bring-your-own-data source's
// data arrives (plan 2026-08-08-byo-freshness-input-events, Step 10):
//
//   1. `DataArrivalModeField`  — "When does new data arrive in this source?"
//   2. `ExpectedArrivalField`  — "By what time is your data usually here?"
//
// Both are purely presentational: the wizard holds them in local state until
// launch, the detail drawer saves each one on its own PATCH. Keeping the save
// out of here is what lets one piece of copy serve both surfaces.
//
// Two rules this file exists to keep:
//
//   - The customer describes THEIR world, never ours. No word from the
//     machinery behind these settings appears in any string below — the
//     customer is answering a question about their own export, not configuring
//     a scheduler.
//   - The time is a bare wall-clock with no zone of its own, and the zone it is
//     read in is one the customer never picked (it is derived from the sources
//     under this connector). So the zone is rendered next to the field, and the
//     RESOLVED promise — which is the latest expected arrival across every
//     source of this connector, not the value in this field — is rendered
//     read-only beside it. Editing this source can move a sibling's build time;
//     that consequence has to be visible while editing, not discovered later.

import { useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Radio,
  Stack,
  Text,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { IconClock } from "@tabler/icons-react";
import type { FreshnessMode } from "@/types/managedSources";

export const DATA_ARRIVAL_MODE_QUESTION =
  "When does new data arrive in this source?";

export const EXPECTED_ARRIVAL_QUESTION =
  "By what time is your data usually here?";

/** Both directions of the no-hard-bound rule, in customer words.
 *
 *  Too EARLY is the stronger warning and comes first: a time that has already
 *  passed stops the build from waiting for the rest of your sources at all.
 *  Too LATE is the one customers do not expect at all, because the cost lands
 *  on a source they are not looking at. */
export const EXPECTED_ARRIVAL_HELP_EARLY =
  "Set it too early and your models start building before your data is in — " +
  "what arrives afterwards is picked up in a later, separate refresh.";

export const EXPECTED_ARRIVAL_HELP_LATE =
  "Set it too late and every other source of this connector waits with it: " +
  "the build starts at the latest time set across all of them, even for data " +
  "that arrived in the morning.";

/** Shown when the platform has never observed this source's data land. Says
 *  what we have seen, not what the source did — we cannot tell the customer
 *  their export failed, only that nothing has reached us. */
export const LAST_ARRIVAL_NEVER = "We have not seen your data land yet.";

/**
 * The "last arrived" line, rendered in the SAME zone the field above is read
 * in — never the browser's.
 *
 * The stored value is a UTC instant. The reader's machine zone is irrelevant
 * here and must not leak in: a bare wall clock in a zone nobody named is the
 * exact ambiguity this whole setting exists to remove. So:
 *
 *   - zone known   → the time of day IN THAT ZONE, with the zone named beside
 *                    it, matching how the promise below is written.
 *   - zone unknown → the date only. A day survives the hour or two a zone
 *                    shift moves things; a time of day does not.
 *
 * Returns null when the value cannot be read as an instant at all, so a
 * malformed timestamp shows nothing rather than "Invalid Date".
 */
export function formatLastArrival(
  isoUtc: string,
  tz?: string | null,
): string | null {
  const at = new Date(isoUtc);
  if (Number.isNaN(at.getTime())) return null;

  // The year only when it is not the current one. Present for a stale arrival,
  // absent for the ordinary case where it would be noise.
  const withYear = at.getUTCFullYear() !== new Date().getUTCFullYear();
  const dateIn = (zone: string) =>
    new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
      ...(withYear ? { year: "numeric" as const } : {}),
      timeZone: zone,
    }).format(at);

  const dateOnly = () => `Your data last arrived on ${dateIn("UTC")}.`;

  if (!tz) return dateOnly();

  try {
    const time = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: tz,
    }).format(at);
    // The DATE is not decoration. Without it "last arrived at 11:40" reads as
    // today, so a source that stopped delivering three days ago still looks
    // healthy — the one case where the customer most needs to notice. Rendered
    // in the same zone as the time, or the two disagree either side of
    // midnight.
    return `Your data last arrived on ${dateIn(tz)} at ${time} (${tz}).`;
  } catch {
    // An unknown zone identifier makes Intl throw. Falling back to the
    // date-only form keeps the fact on screen without ever printing a time
    // whose zone we could not honour.
    return dateOnly();
  }
}

const MODE_OPTIONS: {
  value: FreshnessMode;
  label: string;
  description: string;
}[] = [
  {
    value: "SCHEDULED",
    label: "On a schedule I control",
    description:
      "The data is written at a time you decide, or the table updates continuously.",
  },
  {
    value: "FRESHNESS",
    label: "Once or twice a day, timing varies",
    description:
      "A daily export that can be early one day and late the next. Your models wait for it instead of building at a fixed time.",
  },
];

export type DataArrivalModeFieldProps = {
  value: FreshnessMode;
  onChange: (next: FreshnessMode) => void;
  /** No permission, or another save is already in flight. */
  disabled?: boolean;
  /** This control's own save is in flight. */
  saving?: boolean;
  /** Shown under the question when the reader cannot change it. */
  readOnlyHint?: string | null;
};

export function DataArrivalModeField({
  value,
  onChange,
  disabled,
  saving,
  readOnlyHint,
}: DataArrivalModeFieldProps) {
  return (
    <Radio.Group
      value={value}
      onChange={(next) => onChange(next as FreshnessMode)}
      label={
        <Group gap={6} wrap="nowrap">
          <Text size="sm" fw={500}>
            {DATA_ARRIVAL_MODE_QUESTION}
          </Text>
          {saving && <Loader size={12} aria-label="Saving" />}
        </Group>
      }
      description="Your answer decides whether your models follow the arrival of your data or a fixed time."
    >
      <Stack gap="xs" mt="xs">
        {MODE_OPTIONS.map((o) => (
          <Radio
            key={o.value}
            value={o.value}
            label={o.label}
            description={o.description}
            disabled={disabled || saving}
          />
        ))}
      </Stack>
      {readOnlyHint && (
        <Text size="xs" c="dimmed" mt="xs">
          {readOnlyHint}
        </Text>
      )}
    </Radio.Group>
  );
}

export type ExpectedArrivalFieldProps = {
  /** The SAVED value (`"HH:MM"`), or null when the platform default applies. */
  value: string | null;
  /**
   * Saves the new value; `null` clears it back to the platform default.
   * MUST reject when the save failed — the draft is reset to `value` on a
   * rejection, so a failed save never leaves the field showing the new time.
   */
  onSave: (next: string | null) => Promise<void>;
  /** No permission, or another save is already in flight. */
  disabled?: boolean;
  /** This control's own save is in flight. */
  saving?: boolean;
  /** Resolved daily promise for the whole connector (`"HH:MM"`). */
  floorLocalTime?: string | null;
  /** The zone the value above — and the field below — are read in. */
  floorTz?: string | null;
  /** True while the promise is still being loaded. */
  floorLoading?: boolean;
  /**
   * When this connector's data has usually arrived (`"HH:MM"` in `floorTz`),
   * observed by the platform. A fact beside the promise, never a control.
   */
  observedArrivalLocalTime?: string | null;
  /**
   * When this source's data was last complete, as an ISO-8601 UTC instant.
   * `null` = nothing observed yet (an explicit empty state is rendered).
   * `undefined` = the platform does not report it, so nothing is rendered —
   * silence about the fact, not a claim that nothing arrived.
   * Read-only: a fact beside the field, never a control.
   */
  lastArrivalAt?: string | null;
  /** Shown under the field when the reader cannot change it. */
  readOnlyHint?: string | null;
};

export function ExpectedArrivalField({
  value,
  onSave,
  disabled,
  saving,
  floorLocalTime,
  floorTz,
  floorLoading,
  observedArrivalLocalTime,
  lastArrivalAt,
  readOnlyHint,
}: ExpectedArrivalFieldProps) {
  const [draft, setDraft] = useState(value ?? "");

  // Follow the saved value whenever it changes underneath us (a reload, a save
  // that landed, the drawer switching source). A FAILED save leaves `value`
  // untouched, so this effect cannot undo it — the reject handler below does.
  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const dirty = draft !== (value ?? "");
  const busy = !!saving;
  const locked = !!disabled || busy;

  async function commit(next: string | null) {
    try {
      await onSave(next);
    } catch {
      // The save did not land, so the field must not keep showing what the
      // customer typed — that would read as a saved setting the platform has
      // never heard of. The caller reports the failure itself.
      setDraft(value ?? "");
    }
  }

  return (
    <Stack gap={6}>
      <Group gap="sm" align="flex-end" wrap="nowrap">
        <TimeInput
          label={EXPECTED_ARRIVAL_QUESTION}
          description="Leave empty to use the standard time."
          leftSection={<IconClock size={16} />}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          disabled={locked}
          w={200}
        />
        {/* UX-R1: the value is a bare wall-clock read in a zone the customer
            never chose, and which can change when sources are added or
            removed. Naming it beside the field is the only place it is
            visible. */}
        {floorTz && (
          <Text size="sm" c="dimmed" pb={8}>
            {floorTz}
          </Text>
        )}
        <Button
          size="xs"
          mb={4}
          disabled={locked || !dirty}
          loading={busy}
          onClick={() => commit(draft.trim() === "" ? null : draft.trim())}
        >
          Save
        </Button>
      </Group>

      {value !== null && !dirty && (
        <Anchor
          component="button"
          type="button"
          size="xs"
          disabled={locked}
          onClick={() => {
            setDraft("");
            void commit(null);
          }}
        >
          Use the standard time instead
        </Anchor>
      )}

      {/* What actually happened, next to what the customer declared should
          happen. Held to the same two guards as everything else on this row:
          nothing at all while the zone is still being resolved (a time shown
          in a zone we are about to correct is worse than no time), and no
          time of day when the zone never resolves. */}
      {!floorLoading &&
        lastArrivalAt !== undefined &&
        (lastArrivalAt === null ? (
          <Text size="xs" c="dimmed">
            {LAST_ARRIVAL_NEVER}
          </Text>
        ) : (
          (() => {
            const line = formatLastArrival(lastArrivalAt, floorTz);
            return line ? (
              <Text size="xs" c="dimmed">
                {line}
              </Text>
            ) : null;
          })()
        ))}

      <Text size="xs" c="dimmed">
        {EXPECTED_ARRIVAL_HELP_EARLY}
      </Text>
      <Text size="xs" c="dimmed">
        {EXPECTED_ARRIVAL_HELP_LATE}
      </Text>

      {/* UX-R3: the input is per source, the promise is shared. Read-only, and
          straight from the platform — never recomputed from the field above,
          which would show the customer their own typing back as if it were
          the answer. */}
      {floorLoading ? (
        <Group gap="xs">
          <Loader size={12} />
          <Text size="xs" c="dimmed">
            Working out when your build lands…
          </Text>
        </Group>
      ) : (
        floorLocalTime && (
          <Alert
            variant="light"
            color="blue"
            p="xs"
            title={`Your daily build is ready by ${floorLocalTime}${
              floorTz ? ` (${floorTz})` : ""
            }`}
          >
            {value !== null && value !== floorLocalTime && (
              <Text size="xs">
                Another source of this connector expects its data later, so the
                build waits for that one.
              </Text>
            )}
            {observedArrivalLocalTime && (
              <Text size="xs" data-testid="observed-arrival">
                {`Your data usually lands by ${observedArrivalLocalTime}${
                  floorTz ? ` (${floorTz})` : ""
                }, based on recent days.`}
              </Text>
            )}
          </Alert>
        )
      )}

      {readOnlyHint && (
        <Text size="xs" c="dimmed">
          {readOnlyHint}
        </Text>
      )}
    </Stack>
  );
}
